import { Button } from '@ctw/shared/components/button';
import { APP_TOAST_CONTAINER_MODAL_ID } from '@ctw/shared/components/toast';
import type { ModalLayout } from '@ctw/shared/context/modal-context';
import { MODAL_CONTENT_CONTAINER_TEST_ID, MODAL_WRAPPER_TEST_ID } from '@ctw/shared/test/ids';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { Fragment, type ReactNode } from 'react';
import { ToastContainer, Zoom } from 'react-toastify';

export interface ModalProps {
  isOpen: boolean;
  onOpen?: () => void;
  onAfterOpen?: () => void;
  close: () => void;
  onAfterClose?: () => void;
  layout: ModalLayout;
  label?: string;
  title?: string;
  content: {
    body: ReactNode;
  };
}

export const Modal = ({
  layout,
  content,
  isOpen,
  onOpen,
  onAfterOpen,
  close,
  onAfterClose,
  label,
  title,
}: ModalProps) => (
  <Transition appear={true} show={isOpen} as={Fragment}>
    <Dialog
      as="div"
      className={tw`relative z-modal`}
      onClose={close}
      aria-label={`${label}: ${title}`}
    >
      <TransitionChild
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className={tw`fixed inset-0 transition-opacity`}>
          <div className={tw`h-full w-full bg-content-subtle opacity-75`} />
        </div>
      </TransitionChild>

      <div className={tw`fixed inset-0`}>
        <div
          data-testid={MODAL_WRAPPER_TEST_ID}
          className={twx('h-full max-h-full w-full max-w-full p-4', {
            'overflow-auto': layout.overflowBehavior === 'visible',
          })}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
            afterLeave={onAfterClose}
            beforeEnter={onOpen}
            afterEnter={onAfterOpen}
          >
            <DialogPanel
              data-testid={MODAL_CONTENT_CONTAINER_TEST_ID}
              className={twx(
                'relative z-modal mr-auto ml-auto w-fit max-w-full flex-1 overflow-hidden rounded-md bg-white shadow-xl',
                {
                  // mode:'full' already gets a definite h-full chained from the outer wrapper,
                  // so DialogPanel itself is the correctly-bound scroller there — unchanged.
                  'h-full max-h-full overflow-auto':
                    layout.overflowBehavior === 'scroll' && layout.mode !== 'fit',
                  // mode:'fit' makes this box's own height indefinite (h-min, below), which
                  // breaks percentage-based max-height chaining for every descendant. Making
                  // DialogPanel a flex column lets its flex children size off its *resolved*
                  // height instead of that broken percentage chain, so the inner scrollable div
                  // (not this box) ends up both correctly capped and the real scroll container —
                  // see the inner `data-testid="modal"` div below. Capped at 90vh (rather than
                  // the full viewport) so it doesn't run edge-to-edge on large screens.
                  'flex max-h-[90vh] flex-col':
                    layout.overflowBehavior === 'scroll' && layout.mode === 'fit',
                  'h-full max-h-full overflow-hidden': layout.overflowBehavior === 'hidden',
                  'p-8': !layout.fullbleed,
                  'min-h-full min-w-full': layout.mode === 'full',
                  'h-min': layout.mode === 'fit',
                },
              )}
            >
              <div
                className={twx({
                  'h-full max-h-screen w-full overflow-hidden': ['scroll', 'hidden'].includes(
                    layout.overflowBehavior,
                  ),
                  'h-full max-h-screen min-h-full w-full min-w-full': layout.mode === 'full',
                  // Passthrough flex child for mode:'fit' — propagates DialogPanel's resolved
                  // height to the inner scrollable div via flexbox instead of percentages.
                  'flex min-h-0 flex-1 flex-col':
                    layout.overflowBehavior === 'scroll' && layout.mode === 'fit',
                })}
              >
                {layout.showCloseButton && (
                  // right-4 (not right-1) so this clears the scroll container's native
                  // scrollbar gutter reserved below instead of visually colliding with it.
                  <Button
                    type="button"
                    ariaLabel="Close Modal"
                    variant="circular"
                    size="sm"
                    className={tw`absolute top-1 right-4 z-navigation-sticky flex items-center justify-center bg-transparent p-2 text-content-main shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
                    onClick={close}
                  >
                    <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
                  </Button>
                )}
                <div
                  className={twx({
                    // Every combination except mode:'fit'+scroll keeps this div's original,
                    // byte-for-byte base styling — flex column, capped at the viewport — with
                    // overflow-auto only for overflowBehavior:'scroll'. Unchanged from before
                    // this restructure (this used to be one unconditional base string; it has
                    // to be split out here so the mode:'fit'+scroll case below can override it).
                    'flex h-full max-h-screen flex-col': !(
                      layout.overflowBehavior === 'scroll' && layout.mode === 'fit'
                    ),
                    // scrollbar-gutter:stable reserves the scrollbar's width whether or not this
                    // div currently overflows, so the close button (inset to clear it, above)
                    // never collides with the scrollbar track when it does appear.
                    'overflow-auto [scrollbar-gutter:stable]':
                      layout.overflowBehavior === 'scroll' && layout.mode !== 'fit',
                    // mode:'fit'+scroll: this is the actual, single scroll container — sized by
                    // the flex chain above (DialogPanel -> middle div -> here), not percentages,
                    // so it's correctly capped AND the nearest scrolling ancestor sticky children
                    // bind to (matching what actually scrolls, unlike the old max-h-screen cap).
                    // Deliberately `block`, not `flex` — content.body is an arbitrary consumer
                    // ReactNode we don't control the markup of; making it a flex item here would
                    // give it a default flex-shrink:1 with no min-height:0 override, so it'd get
                    // squeezed down to the available space instead of rendering at its natural
                    // height and letting this div scroll the overflow.
                    'block min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]':
                      layout.overflowBehavior === 'scroll' && layout.mode === 'fit',
                  })}
                  data-testid="modal"
                >
                  {content.body}
                </div>
                <ToastContainer
                  position="bottom-left"
                  autoClose={40000}
                  hideProgressBar={true}
                  closeOnClick={true}
                  rtl={false}
                  limit={3}
                  transition={Zoom}
                  pauseOnFocusLoss={true}
                  pauseOnHover={true}
                  theme="colored"
                  enableMultiContainer={true}
                  containerId={APP_TOAST_CONTAINER_MODAL_ID}
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </Transition>
);
